import { Tab, Tabs } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import { ROUTES } from 'consts'
import { MobileTabs } from 'consts/appConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePlanLimits from 'hooks/usePlanLimits'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { createIdForTest } from 'utils/testingUtils.js'

const useStyles = makeStyles(theme => ({
  tabs: {
    width: '100%'
  },
  tab: {
    flex: 1,
    fontFamily: 'Dazzed',
    fontSize: '14px',
    textTransform: 'none',
    padding: '14px 14px 10px 14px',
    borderBottom: '4px solid var(--border-color-20)',
    color: 'var(--text-color-2)',
    [theme.breakpoints.down('xs')]: {
      fontSize: '13px',
      padding: '14px 4px 10px 4px',
      minWidth: 0
    }
  },
  tabSelected: {
    color: 'var(--text-color-7)',
    fontWeight: 600
  },
  tabIndicator: {
    borderBottom: '4px solid var(--border-color-1)'
  }
}))

const AppTabsMobile = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { pathname } = useLocation()

  const idFromUrl = useMemo(() => pathname.slice(ROUTES.PATIENTS.length).split('/')[1], [pathname])

  const patient = useSelector(state => state.patientsReducer.patient)
  const rooms = useSelector(state => state.chatReducer.rooms)
  const tab = useSelector(state => state.appReducer.mobile.selectedTab)

  // Mirrors the desktop PatientSidebar's Copilot gating — the tab is only useful
  // for HI practices with the copilotControl feature flag enabled.
  const { copilotControl: copilotFF } = useFeatureFlags()
  const { isHI } = usePlanLimits()

  const navigateToTab = useCallback(
    pathname => {
      history.push({
        pathname,
        search: window.location.search
      })
    },
    [history]
  )

  const tabs = useMemo(
    () =>
      [
        {
          value: MobileTabs.INBOX,
          label: t('header.tabs.inbox'),
          select: () => navigateToTab(ROUTES.PATIENTS),
          isSelected: () => history.location.pathname === ROUTES.PATIENTS
        },
        {
          value: MobileTabs.TIMELINE,
          label: t('header.tabs.timeline'),
          select: () =>
            navigateToTab(
              `${ROUTES.PATIENTS}/${patient?.id ? patient.id : idFromUrl ? idFromUrl : rooms[0].patientId}/${
                ROUTES.PATIENT_TIMELINE
              }`
            ),
          isSelected: () => history.location.pathname.includes(ROUTES.PATIENT_TIMELINE)
        },
        {
          value: MobileTabs.CHAT,
          label: t('header.tabs.chat'),
          select: () =>
            navigateToTab(`${ROUTES.PATIENTS}/${patient?.id ? patient.id : rooms[0].patientId}/${ROUTES.PATIENT_CHAT}`),
          isSelected: () => history.location.pathname.includes(ROUTES.PATIENT_CHAT)
        },
        {
          value: MobileTabs.NOTES,
          label: t('header.tabs.notes'),
          select: () =>
            navigateToTab(
              `${ROUTES.PATIENTS}/${patient?.id ? patient.id : rooms[0].patientId}/${ROUTES.PATIENT_NOTES}`
            ),
          isSelected: () => history.location.pathname.includes(ROUTES.PATIENT_NOTES)
        },
        {
          value: MobileTabs.COPILOT,
          label: t('header.tabs.copilot'),
          // Show the tab when the user is HI with copilotFF OR when the URL
          // is already on /copilot. The OR-clause covers the race where a
          // deep-link from an email/SMS lands on /patients/:id/copilot
          // before isHI/copilotFF have resolved (those depend on grinPlan
          // and assorted FF data that arrive in separate Redux dispatches).
          // Without it, the Copilot tab is filtered out on initial render
          // and the active-tab effect below falls back to INBOX, leaving
          // the bottom nav incorrectly highlighting Inbox while the Copilot
          // screen is rendered. Once isHI && copilotFF resolve, the first
          // half of the OR takes over and the tab stays visible normally.
          condition: () => (isHI && copilotFF) || pathname.includes(`/${ROUTES.PATIENT_COPILOT}`),
          select: () =>
            navigateToTab(
              `${ROUTES.PATIENTS}/${patient?.id ? patient.id : rooms[0].patientId}/${ROUTES.PATIENT_COPILOT}`
            ),
          isSelected: () => history.location.pathname.includes(ROUTES.PATIENT_COPILOT)
        }
      ].filter(tab => (tab.condition ? tab.condition() : true)),
    // `pathname` is in deps so the Copilot tab's pathname-based fallback
    // re-evaluates when a deep-link redirects from `?patientView=copilot`
    // to `/patients/:id/copilot` — without it the useMemo would return a
    // stale tabs array (idFromUrl stays the same in that transition).
    [t, patient, history, rooms, idFromUrl, navigateToTab, isHI, copilotFF, pathname]
  )

  const handleTabSelection = useCallback(
    (event, value) => {
      tabs.find(tab => tab.value === value)?.select()
    },
    [tabs]
  )

  useEffect(() => {
    dispatch(Actions.mobileSetSelectedTab(tabs.find(tab => tab.isSelected())?.value || MobileTabs.INBOX))
  }, [history.location.pathname, tabs, dispatch])

  return (
    <Tabs value={tab} onChange={handleTabSelection} classes={{ root: classes.root, indicator: classes.tabIndicator }}>
      {tabs.map(tab => (
        <Tab
          id={createIdForTest({ componentId: 'mobile-responsive-tab', title: tab.label })}
          disableRipple
          key={`app-tab-${tab.value}`}
          label={tab.label}
          value={tab.value}
          classes={{
            root: classes.tab,
            selected: classes.tabSelected
          }}
        />
      ))}
    </Tabs>
  )
}

export default AppTabsMobile
